import axios from 'axios';
import {
  Product,
  EcommerceProduct,
  Testimonial,
  createProductFromJson,
  createEcommerceProductFromJson,
  createTestimonialFromJson,
  ABTestInfo,
} from '../types';
import testimonialsSeed from '../data/testimonials.json';

const getDefaultApiBaseUrl = (): string => {
  // 1) Explicit override via env (works both locally and in production)
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // 2) Running in a browser
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;

    // Local development on the same machine (CRA on :3000, FastAPI on :8000)
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:8000';
    }

    // Access from another device on the same LAN (e.g. phone -> 192.168.x.x:3000)
    // In this case, use the same host but port 8000 for the backend.
    const isLanIp = /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
    if (isLanIp) {
      return `http://${host}:8000`;
    }
  }

  // 3) Default for Firebase Hosting (rewrites /api -> Cloud Function)
  return '/api';
};

export const API_BASE_URL = getDefaultApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface ChatResponse {
  response: string;
  products?: Product[];
  search_results?: EcommerceProduct[];
  tag_result?: any;
  number_of_cards?: number; // Backend'den gelen ghost cards sayısı
}

// Two-phase suggestions have been removed at the backend; we keep only a simple chat API.

export interface ABTestStartRequest {
  product_id: string;
  test_field: string;
  a_variant: string;
  b_variant: string;
  start_date: string;
}

export interface ABTestSuggestionRequest {
  product_id: string;
  current_text: string;
  test_field: string;
}

export interface ABTestSuggestionResponse {
  suggestion: string;
  reasoning: string;
  confidence: number;
}

// Single-phase chat function.
// Backend now returns text-only products without images via /generate_suggestions_text.
export const sendChatMessage = async (message: string): Promise<ChatResponse> => {
  try {
    // Call the canonical text-only endpoint directly
    const response = await api.post('/generate_suggestions_text', { 
      description: message
    });
    
    return {
      // response: `"${message}" için ${response.data.number_of_cards || 0} ürün önerisi oluşturuldu:`,
      response: `🤔 Aradığın 'o şey' bunlardan biri olabilir mi?`,

      products: (response.data.products || []).map(createProductFromJson),
      search_results: [],
      tag_result: undefined,
      number_of_cards: response.data.number_of_cards // Backend'den gelen ghost cards sayısı
    };
  } catch (error: any) {
    console.error('API Error:', error);

    // Varsayılan genel hata mesajı
    let errorMessage =
      'Üzgünüm, şu anda hizmet kullanılamıyor. Lütfen daha sonra tekrar deneyin.';

    // Backend'den gelen 503 hata mesajını mümkünse aynen göster
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;
      const data: any = error.response.data;

      const backendDetail =
        typeof data === 'string'
          ? data
          : typeof data?.detail === 'string'
          ? data.detail
          : undefined;

      if (status === 503 && backendDetail) {
        errorMessage = backendDetail;
      }
    }

    return {
      response: errorMessage,
      products: [],
      search_results: [],
      tag_result: undefined
    };
  }
};

/**
 * Backwards-compatible wrapper for the old "two-phase" chat API.
 *
 * The real two-phase endpoint has been removed on the backend, so this
 * implementation simply delegates to `sendChatMessage` and (optionally)
 * invokes the ghost-card callback once using the returned `number_of_cards`.
 *
 * This allows older UI code that still depends on `sendChatMessageTwoPhase`
 * to compile and behave reasonably without changing every call site.
 */
export const sendChatMessageTwoPhase = async (
  message: string,
  onGhostCards?: (ghostCardCount: number) => void
): Promise<ChatResponse> => {
  const response = await sendChatMessage(message);

  if (onGhostCards && typeof response.number_of_cards === 'number') {
    onGhostCards(response.number_of_cards);
  }

  return response;
};

/**
 * Deprecated image + message helper.
 *
 * The image upload and tag generation endpoints have been removed from
 * the backend, so this helper now returns a graceful fallback response
 * instead of performing any network request.
 */
export const sendImageWithMessage = async (
  _file: File,
  _message?: string
): Promise<ChatResponse> => {
  console.warn(
    '[sendImageWithMessage] Image-based search is currently disabled; returning fallback response.'
  );

  return {
    response:
      'Görsel tabanlı arama şu anda devre dışı. Lütfen aradığınız ürünü metinle tarif etmeyi deneyin.',
    products: [],
    search_results: [],
    tag_result: undefined
  };
};

/**
 * Deprecated A/B test helpers.
 *
 * The corresponding backend endpoints have been removed. These functions now
 * act as no-ops (or return empty data) so that existing UI components can
 * still compile without runtime crashes.
 */
export const startABTest = async (_request: ABTestStartRequest): Promise<void> => {
  console.warn(
    '[startABTest] A/B test backend endpoint is disabled. This call has no effect.'
  );
};

export const getABTestAISuggestion = async (
  request: ABTestSuggestionRequest
): Promise<ABTestSuggestionResponse> => {
  console.warn(
    '[getABTestAISuggestion] A/B test suggestion endpoint is disabled. Returning a dummy suggestion.'
  );

  return {
    suggestion: request.current_text,
    reasoning:
      'A/B test öneri servisi devre dışı olduğu için mevcut metni öneri olarak geri döndürüyoruz.',
    confidence: 0
  };
};

export const getActiveABTests = async (): Promise<Record<string, ABTestInfo>> => {
  console.warn(
    '[getActiveABTests] A/B test state endpoint is disabled. Returning an empty test list.'
  );

  return {};
};

// Search API calls
export const searchProducts = async (query: string): Promise<Product[]> => {
  const response = await api.get(`/search?query=${encodeURIComponent(query)}`);
  return response.data.map(createProductFromJson);
};

export const searchEcommerceProducts = async (query: string): Promise<EcommerceProduct[]> => {
  const response = await api.get(`/search-ecommerce?query=${encodeURIComponent(query)}`);
  return response.data.map(createEcommerceProductFromJson);
};

export const getEcommerceProducts = async (limit = 10): Promise<any> => {
  const response = await api.get(`/ecommerce_products?limit=${limit}`);
  return response.data;
};

const STATIC_TESTIMONIALS_RAW: any[] = (testimonialsSeed as any[]) || [];

export const getRandomTestimonials = async (limit = 2): Promise<Testimonial[]> => {
  if (!STATIC_TESTIMONIALS_RAW.length) {
    return [];
  }

  // Shuffle on the client so we get varied combinations without any network call.
  const shuffled = [...STATIC_TESTIMONIALS_RAW].sort(() => Math.random() - 0.5);
  const effectiveLimit = Math.max(1, Math.min(limit, shuffled.length));

  return shuffled
    .slice(0, effectiveLimit)
    .map((item) => createTestimonialFromJson(item));
};

export const submitContactEmail = async (email: string): Promise<void> => {
  await api.post('/contact-email', { email });
};

export default api;